import { useCallback, useEffect, useState } from 'react';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { Flex, View } from '@adobe/react-spectrum';
import {
  AtollonIcon,
  PrintIcon,
  EditIcon,
  ToolIcon,
  TrashIcon,
  MassmailGreen,
  MassmailRed,
  TwitterIcon,
  FacebookIcon,
  LinkdinIcon,
  ClientIconBlue,
} from '../../../../components/protected/Icons/IconsLib';
import { ToastQueue } from '@react-spectrum/toast';
import { useTranslation } from 'react-i18next';
import { ComboBox, Item, Key } from '@adobe/react-spectrum';
import ButtonsComponent from './ButtonsComponent';
import { GetContextPathRequest } from '../../../../services/soap/project/requests/GetContextPathRequest';
import { GetFolderRequest } from '../../../../services/soap/project/requests/GetFolderRequest';
import { UpdateFolderRequest } from '../../../../services/soap/project/requests/UpdateFolderRequest';
import { CheckWorkFlowQueueRequest } from '../../../../services/soap/features/requests/CheckWorkFlowQueueRequest';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../state/store';
import { setSelectedItem } from '../../../../state/Finder/finderSlice';
import styles from './HeaderToolbarComponent.module.css';
import { usePreloadAssets } from '../../../../hooks/UsePreloadAssets';
interface IFolderComponentProps {
  headerData: any;
  statusColor: (color: string) => string;
  onPrint?: (item: FPAData) => void;
  onEdit?: (item: FPAData) => void;
  onDelete?: (item: FPAData) => void;
  onTool?: (item: FPAData) => void;
  onAI?: (item: FPAData) => void;
  selectedItem?: FPAData;
}

const FolderComponent = ({ headerData, statusColor, onPrint, onEdit, onDelete, onTool, onAI, selectedItem }: IFolderComponentProps) => {
  const { store, projectService, featureService } = useDependency();
  const { folderStatusMapping2 } = usePreloadAssets();
  const [folderStatusList, setFolderStatusList] = useState<any>([]);
  const [fullPath, setFullPath] = useState<string>('');
  const [folderData, setFolderData] = useState<any>([]);
  let [activityStatus, setActivityStatus] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const getFolderStatusArray = useCallback(async () => {
    if(folderStatusMapping2){
      const statusArray: any = Object.keys(folderStatusMapping2).map((key: string) => ({ id: key, name: (folderStatusMapping2 as any)[key].name, color: (folderStatusMapping2 as any)[key].color, status: (folderStatusMapping2 as any)[key].status }));
      var filtered_status = statusArray.filter((item: any) => (item.status.TYPE && item.status.TYPE.find((type: any) => type.id === headerData?.folderType)));
      setFolderStatusList(filtered_status);
    }
  }, [folderStatusMapping2]);

  useEffect(() => {
    (async () => {
      if(headerData && headerData.id && folderStatusMapping2 && getFolderStatusArray){
        headerData && (await getFullPath());
        await getFolderData();
        getFolderStatusArray();
        setActivityStatus(headerData?.customState);
      }
    })();
  }, [headerData?.id, folderStatusMapping2, getFolderStatusArray]);

  const getFullPath = async () => {
    const response = await projectService.getContextPath(new GetContextPathRequest(store.Server, store.SessionId, headerData?.id || 0));
    setFullPath(response.LIST.map(item => item.CONTEXT.name).join(' > '));
  };

  async function getFolderData() {
    if (selectedItem) {
      let { FOLDER } = await projectService.getFolder(new GetFolderRequest(store.Server, store.SessionId, selectedItem.id));
      setFolderData(FOLDER);
    }
  }

  async function updateFolderStatus(key: Key | null) {
    if (key === null) {
      // Handle clear action
      let resFolder = await projectService.updateFolder(new UpdateFolderRequest(store.Server, store.SessionId, { ...folderData, customState: '' }));
      if (resFolder.result == 'OK') {
        await featureService.checkWorkFlowQueue(new CheckWorkFlowQueueRequest(store.Server, store.SessionId));
        ToastQueue.positive(t('status_updated_successfully', { ns: 'layout_components' }));
        setActivityStatus('');
        dispatch(setSelectedItem({ item: selectedItem ? { ...selectedItem, statusName: '', statusColor: '' } : null, show_context: false }));
      } else {
        ToastQueue.negative(resFolder.EXCEPTION?.message, { timeout: 1000 });
      }
      return;
    }

    const value = key.toString();
    let resFolder = await projectService.updateFolder(new UpdateFolderRequest(store.Server, store.SessionId, { ...folderData, customState: value }));
    let status = folderStatusList.find((status: any) => status.id === value);
    if (resFolder.result == 'OK') {
      await featureService.checkWorkFlowQueue(new CheckWorkFlowQueueRequest(store.Server, store.SessionId));
      ToastQueue.positive(t('status_updated_successfully', { ns: 'layout_components' }));
      setActivityStatus(value);
      dispatch(setSelectedItem({ item: selectedItem ? { ...selectedItem, statusName: status.name, statusColor: statusColor(status.color) } : null, show_context: false }));
    } else {
      ToastQueue.negative(resFolder.EXCEPTION?.message, { timeout: 1000 });
    }
  }

  const getAddress = () => {
    return headerData?.CONTACT?.COMPANY?.street !== undefined && headerData?.CONTACT?.COMPANY?.street !== null
      ? headerData?.CONTACT?.COMPANY?.street + ' ' + headerData?.CONTACT?.COMPANY?.city + ' ' + headerData?.CONTACT?.COMPANY?.zip + ' ' + headerData?.CONTACT?.COMPANY?.country
      : '';
  };

  const copyUrl = () => {
      const url = `${window.location.origin}/finder?id=${selectedItem?.id}`;
      navigator.clipboard.writeText(url);
      ToastQueue.positive(t('link_copied_to_clipboard', { ns: 'layout_components' }), { timeout: 1000 });
    }

  return (
    <Flex width="100%">
      <View UNSAFE_className={styles.header_main}>
        <View UNSAFE_className={styles.header_child}>
          <View UNSAFE_className={styles.frame_parent}>
            <View UNSAFE_className={styles.icon_activity_parent}>
              <ClientIconBlue size={32} />
              <b title={headerData?.name} className={styles.activity_name}>
                {headerData?.name.length > 45 ? headerData?.name.substring(0, 45) + '...' : headerData?.name}
              </b>
            </View>
            <View UNSAFE_className={styles.folder_name_parent}>
              <View UNSAFE_className={styles.folder_name} UNSAFE_style={{ wordBreak: 'break-all' }}>
                {fullPath.length > 50 ? fullPath.substring(0, 50) + '...' : fullPath}
              </View>
            </View>
          </View>
          <Flex direction={'row'} justifyContent={'space-between'} UNSAFE_className={styles.tag_parent} marginTop={20}>
            <View UNSAFE_style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
              <View UNSAFE_className={styles.tag}>
                <View UNSAFE_className={styles.activity_type}>
                  <View UNSAFE_className={styles.activity_type_text}>{headerData?.folderTypeName}</View>

                  {headerData?.referenceId ? (
                    <>
                      <View UNSAFE_className={styles.divider}></View> <View UNSAFE_className={styles.ref_id}>{headerData?.referenceId}</View>
                    </>
                  ) : (
                    <View UNSAFE_className={styles.divider_transparent}></View>
                  )}
                </View>
              </View>

              <View UNSAFE_className={styles.io_parent}>
                {headerData?.CONTACT?.COMPANY?.COMPANYINFO.regNumber !== undefined ? (
                  <>
                    <View UNSAFE_className={styles.io_label}>IČO</View>
                    <View UNSAFE_className={styles.io_text}>{headerData?.CONTACT?.COMPANY?.COMPANYINFO.regNumber}</View>
                  </>
                ) : (
                  ''
                )}
              </View>

              <View UNSAFE_className={styles.di_parent}>
                {headerData?.CONTACT?.COMPANY?.COMPANYINFO.VATNumber !== undefined ? (
                  <>
                    <View UNSAFE_className={styles.di_label}>DIČ</View>
                    <View UNSAFE_className={styles.di_text}>{headerData?.CONTACT?.COMPANY?.COMPANYINFO.VATNumber}</View>
                  </>
                ) : (
                  ''
                )}
              </View>

              <View UNSAFE_className={styles.address_parent}>
                {getAddress() !== '' ? (
                  <>
                    <View UNSAFE_className={styles.address_child}>{t('address', { ns: 'layout_components' })}</View>
                    <View UNSAFE_className={styles.address}>{getAddress()}</View>
                  </>
                ) : (
                  ''
                )}
              </View>
            </View>

            <View UNSAFE_className={styles.socials_icons}>
              <View UNSAFE_className={styles.icon_twitter}>
                <TwitterIcon size={24} />
              </View>

              <View UNSAFE_className={styles.icon_facebook}>
                <FacebookIcon size={24} />
              </View>

              <View UNSAFE_className={styles.icon_linkedin}>
                <LinkdinIcon size={24} />
              </View>
            </View>
          </Flex>
        </View>
        <View UNSAFE_className={styles.icon_list_parent}>
          {headerData?.CONTACT?.COMPANY?.adsAllowed === 'N' ? (
            <View UNSAFE_className={styles.ad_not_allowed}>
              <MassmailRed size={20} />
            </View>
          ) : (
            <View UNSAFE_className={styles.ad_allowed}>
              <MassmailGreen size={20} />
            </View>
          )}

          <ComboBox
            width={'200px'}
            selectedKey={activityStatus || null}
            onSelectionChange={updateFolderStatus}
            allowsCustomValue={false}
            aria-label={t('status', { ns: 'layout_components' })}
          >
            {folderStatusList.map((item: any) => (
              <Item key={item.id} textValue={item.name}>
                <View UNSAFE_style={{ height: 40, width: 200, backgroundColor: statusColor(item.color), padding: 8 }}>
                {item.name}
                </View>
              </Item>
            ))}
          </ComboBox>

          <ButtonsComponent
            styles={styles}
            AtollonIcon={AtollonIcon}
            PrintIcon={PrintIcon}
            EditIcon={EditIcon}
            ToolIcon={ToolIcon}
            TrashIcon={TrashIcon}
            onPrint={onPrint}
            onEdit={onEdit}
            onDelete={onDelete}
            onTool={onTool}
            onAI={onAI}
            selectedItem={selectedItem}
            onCopyLink={copyUrl}
          />
        </View>
      </View>
    </Flex>
  );
};

export default FolderComponent;
